import { Box, Button, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useState } from "react";
import { MdAssignment } from "react-icons/md";
import { useDispatch } from "react-redux";
import { examRedoRequest } from "../../../modules/students-modules/exams/Actions";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  cream: "#FFF7ED",
  paper: "#FFFFFF",
  muted: "#78716C",
};

function examTypeLabel(exam) {
  return Number(exam?.type) === 2 ? "Homework/Classified" : "Exam";
}

function formatMetaLine(exam) {
  const parts = [];
  if (exam?.total_count != null && String(exam.total_count).trim() !== "") {
    parts.push(`${exam.total_count} questions`);
  }
  if (exam?.timer == 1 && exam?.time != null && String(exam.time).trim() !== "") {
    parts.push(`${exam.time} min`);
  }
  if (exam?.created_at) {
    parts.push(dayjs(exam.created_at).format("D MMM YYYY"));
  }
  return parts.length ? parts.join(" • ") : examTypeLabel(exam);
}

const ExamMainData = ({ exam, state, solveExamFun, checkExamAnswersFun }) => {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const isSolved = state === "Solved";

  return (
    <Box
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      w="260px"
      maxW="100%"
      transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
      willChange="transform"
      _hover={{
        borderColor: `${C.primary}66`,
        transform: "scale(1.02)",
        boxShadow: "0 18px 46px rgba(232, 93, 4, 0.18), 0 0 0 1px rgba(232, 224, 213, 0.35)",
      }}
      _active={{ transform: "scale(1.01)" }}
    >
      <Box
        position="relative"
        h="160px"
        overflow="hidden"
        bgGradient="linear(to-br, rgba(232, 93, 4, 0.15), rgba(154, 52, 18, 0.05))"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Box
          position="absolute"
          top={3}
          left={3}
          bg={C.secondary}
          color="white"
          fontSize="9px"
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
          px={2}
          py={0.5}
          borderRadius="full"
        >
          {examTypeLabel(exam)}
        </Box>
        <Box
          position="absolute"
          top={3}
          right={3}
          bg={isSolved ? C.primary : C.muted}
          color="white"
          fontSize="9px"
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
          px={2}
          py={0.5}
          borderRadius="full"
        >
          {state}
        </Box>
        <MdAssignment color={C.secondary} size={48} />
      </Box>

      <Box
        p={4}
        borderTopWidth="1px"
        borderTopColor="rgba(232, 224, 213, 0.75)"
        display="flex"
        flexDirection="column"
        minH="auto"
        flex="1"
      >
        <Text fontSize="sm" fontWeight="800" color={C.secondary} noOfLines={2} mb={1}>
          {exam?.name}
        </Text>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={`${C.secondary}99`}
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={exam?.score || exam?.total_count || exam?.timer == 1 ? 1 : 3}
        >
          {formatMetaLine(exam)}
        </Text>

        {exam?.score ? (
          <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
            Score: {exam?.right_answers == null ? "--" : exam?.right_answers} / {exam?.score}
          </Text>
        ) : exam?.timer == 1 || exam?.total_count ? (
          <VStack align="stretch" spacing={0.5} mb={3}>
            {exam?.timer == 1 && (
              <Text fontSize="xs" fontWeight="600" color={C.muted}>
                Exam time: {exam?.time} min
              </Text>
            )}
            {exam?.total_count && (
              <Text fontSize="xs" fontWeight="600" color={C.muted}>
                Questions: {exam?.total_count}
              </Text>
            )}
          </VStack>
        ) : null}

        <Box mt="auto">
          {isSolved ? (
            <VStack spacing={2} align="stretch">
              <Button
                w="full"
                size="sm"
                bg={C.primary}
                color="white"
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                onClick={() => checkExamAnswersFun(exam)}
                isDisabled={exam?.correction == 1 ? false : true}
                _hover={{ bg: C.secondary }}
                _disabled={{ bg: C.primary, opacity: 0.55, cursor: "not-allowed" }}
              >
                Check your answers
              </Button>
              <Button
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isLoading={isLoading}
                isDisabled={exam?.redo_flag == 1 ? false : true}
                onClick={() => {
                  setLoading(true);
                  let formData = new FormData();
                  formData.append("exam_id", exam?.id);
                  const action = () => {
                    setLoading(false);
                  };
                  dispatch(examRedoRequest(formData, action));
                }}
                _hover={{ bg: C.secondary, color: C.cream, borderColor: C.secondary }}
              >
                Request Redo
              </Button>
            </VStack>
          ) : (
            <Button
              w="full"
              size="sm"
              variant="outline"
              borderWidth="2px"
              borderColor={C.primary}
              color={C.primary}
              fontWeight="800"
              textTransform="uppercase"
              fontSize="10px"
              borderRadius="lg"
              isDisabled={exam?.enable == 0 ? false : true}
              onClick={() => {
                localStorage.setItem("startTime", new Date());
                solveExamFun(exam);
              }}
              _hover={{ bg: `${C.primary}12` }}
              _disabled={{
                borderColor: C.primary,
                color: C.primary,
                opacity: 0.55,
                cursor: "not-allowed",
              }}
            >
              Start your exam
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExamMainData;
